/**
 * MicPermissionModal — graceful fallback when the user denies microphone
 * access on Session Config submit (PRD §5.3).
 *
 * Two CTAs:
 *   - "Try again" → re-trigger the OS mic prompt
 *   - "Back to config" → close the modal; user can adjust settings or
 *     decide not to start the session
 *
 * Copy follows the PRD spirit: explain WHY mic is needed without forcing
 * the user into a corner. V1 cannot proceed without recording (PRD §10.1
 * makes recording the input to grading), so the modal blocks the
 * navigation rather than letting the user continue silently.
 */

import { cn } from '@/lib/utils';

interface MicPermissionModalProps {
  onRetry: () => void;
  onCancel: () => void;
  /** True while the retry request is in flight (disable buttons). */
  busy?: boolean;
}

export function MicPermissionModal({
  onRetry,
  onCancel,
  busy,
}: MicPermissionModalProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-surface-overlay px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mic-modal-title"
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-surface-elevated p-6 shadow-2xl">
        <h2
          id="mic-modal-title"
          className="mb-2 text-lg font-bold text-text-primary"
        >
          Microphone access required
        </h2>
        <p className="mb-3 text-sm text-text-secondary">
          PokerSharp records your voice during each decision so it can
          transcribe and grade what you said. Without microphone access, the
          session can't run.
        </p>
        <p className="mb-5 text-xs text-text-muted">
          If you denied the prompt, you may need to update your browser's
          site settings before retrying.
        </p>
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className={cn(
              'rounded-md border border-border bg-surface-app px-4 py-2 text-sm font-semibold',
              'text-text-secondary hover:text-text-primary',
              'focus:outline-none focus:ring-2 focus:ring-border-focus focus:ring-offset-1',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            Back to config
          </button>
          <button
            type="button"
            onClick={onRetry}
            disabled={busy}
            className={cn(
              'rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold',
              'text-text-on-brand hover:bg-brand-primary-hover',
              'focus:outline-none focus:ring-2 focus:ring-border-focus focus:ring-offset-1',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            {busy ? 'Requesting…' : 'Try again'}
          </button>
        </div>
      </div>
    </div>
  );
}
